import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { SnagStatusBadge, SnagPriorityBadge } from '../../snags-list-creation/components/SnagStatusBadge';
import { useLanguage } from '../../../contexts/LanguageContext';

const SnagHeader = ({ snag, onStatusChange, canEdit, updating }) => {
  const { t } = useLanguage();

  const nextStatus = {
    open: 'in_progress',
    in_progress: 'resolved',
    resolved: null
  };

  const nextStatusLabel = {
    open: t('markInProgress'),
    in_progress: t('markResolved'),
    resolved: null
  };

  const nextStatusIcon = {
    open: 'Play',
    in_progress: 'CheckCircle2',
    resolved: null
  };

  const next = nextStatus?.[snag?.status];

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-mono text-muted-foreground bg-muted px-2 py-0.5 rounded">
              #{snag?.id?.substring(0, 8)?.toUpperCase()}
            </span>
            <SnagStatusBadge status={snag?.status} />
            <SnagPriorityBadge priority={snag?.priority} />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">{snag?.title}</h1>
          <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <Icon name="Building2" size={14} />
              {snag?.sites?.site_name || t('unknownSite')}
            </span>
            {snag?.location && (
              <span className="flex items-center gap-1.5">
                <Icon name="MapPin" size={14} />
                {snag?.location}
              </span>
            )}
            <span className="flex items-center gap-1.5">
              <Icon name="User" size={14} />
              {t('reportedBy')} {snag?.user_profiles?.full_name || t('unknown')}
            </span>
            <span className="flex items-center gap-1.5">
              <Icon name="Calendar" size={14} />
              {new Date(snag?.created_at)?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
            </span>
          </div>
          {snag?.assigned_user?.full_name && (
            <div className="mt-3 flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-accent/20 flex items-center justify-center">
                <Icon name="User" size={14} color="var(--color-accent)" />
              </div>
              <span className="text-sm text-foreground">{t('assignedTo')} <strong>{snag?.assigned_user?.full_name}</strong></span>
            </div>
          )}
        </div>

        {canEdit && next && (
          <div className="flex gap-2">
            <Button
              variant="primary"
              size="sm"
              iconName={nextStatusIcon?.[snag?.status]}
              onClick={() => onStatusChange(next)}
              disabled={updating}
            >
              {updating ? t('updating') : nextStatusLabel?.[snag?.status]}
            </Button>
          </div>
        )}
      </div>

      {snag?.status === 'resolved' && snag?.resolved_at && (
        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-sm text-green-700 flex items-center gap-2">
            <Icon name="CheckCircle2" size={16} />
            {t('resolvedOn')} {new Date(snag?.resolved_at)?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
            {snag?.resolver?.full_name && ` ${t('by')} ${snag?.resolver?.full_name}`}
          </p>
          {snag?.resolution_notes && (
            <p className="text-sm text-green-700 mt-1 ml-6">{snag?.resolution_notes}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default SnagHeader;
